import React, { useEffect, useState } from "react";
import { Row, Col, ListGroup, Button, Badge, Alert, Form } from "react-bootstrap";
import { FaPlus, FaTrash } from "react-icons/fa";
import { permissionGroupService } from "../../services/permissionGroup.service";
import { userPermissionGroupService } from "../../services/userPermissionGroup.service";
import { userService } from "../../services/user.service";

function UserGroupsTab({ user, preSelectedGroup }) {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(preSelectedGroup || null);
  const [groupUsers, setGroupUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (preSelectedGroup) {
      setSelectedGroup(preSelectedGroup);
    }
  }, [preSelectedGroup]);

  useEffect(() => {
    if (selectedGroup) {
      loadGroupUsers(selectedGroup.groupId);
    }
  }, [selectedGroup]);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load groups and users for the current client only
      const [groupsResp, usersResp] = await Promise.all([
        permissionGroupService.getAll({ clientId: user.clientId, clientGuid: user.clientGuid }),
        userService.get({
          clientId: user.clientId,
          clientGuid: user.clientGuid,
        })
      ]);

      setGroups(groupsResp.data || groupsResp);

      // Handle paginated user response
      const userData = usersResp.data || usersResp;
      const userList = userData.data || userData;
      const finalUserList = Array.isArray(userList) ? userList : [];
      setUsers(finalUserList);

      setError(null);
    } catch (err) {
      setError("Failed to load data");
    } finally{
      setLoading(false);
    }
  };

  const loadGroupUsers = async (groupId) => {
    try {
      const response = await userPermissionGroupService.getUsersInGroup(groupId);
      const grpUsers = response.data || response;
      setGroupUsers(grpUsers);

      // Calculate available users (not yet assigned to group)
      const assignedUserIds = grpUsers.map(gu => gu.userId);
      const available = users.filter(u => !assignedUserIds.includes(u.userId));
      setAvailableUsers(available);
    } catch (err) {
      setError("Failed to load group users");
    }
  };

  // Recalculate available users when users list changes
  useEffect(() => {
    if (selectedGroup && users.length > 0) {
      const assignedUserIds = groupUsers.map(gu => gu.userId);
      const available = users.filter(u => !assignedUserIds.includes(u.userId));
      setAvailableUsers(available);
    }
  }, [users, groupUsers, selectedGroup]);

  const handleAssignUser = async (userId) => {
    try {
      await userPermissionGroupService.assign({
        userId: userId,
        groupId: selectedGroup.groupId,
      });
      loadGroupUsers(selectedGroup.groupId);
    } catch (err) {
      setError("Failed to assign user");
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await userPermissionGroupService.remove(userId, selectedGroup.groupId);
      loadGroupUsers(selectedGroup.groupId);
    } catch (err) {
      setError("Failed to remove user");
    }
  };

  const getUserDetails = (userId) => {
    return users.find(u => u.userId === userId);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      {error && <Alert variant="danger">{error}</Alert>}

      <Row>
        {!preSelectedGroup && (
          <Col md={4}>
            <h5>Select Permission Group</h5>
            <ListGroup>
              {groups.filter(g => g.active).map((group) => (
                <ListGroup.Item
                  key={group.groupId}
                  active={selectedGroup?.groupId === group.groupId}
                  onClick={() => setSelectedGroup(group)}
                  style={{ cursor: "pointer" }}
                >
                  {group.groupName}
                  <br />
                  <small className="text-muted">{group.groupDescription}</small>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Col>
        )}

        <Col md={preSelectedGroup ? 12 : 8}>
          {selectedGroup ? (
            <>
              {!preSelectedGroup && (
                <h5>
                  Users in: {selectedGroup.groupName}
                  <Badge bg="secondary" className="ms-2">
                    {groupUsers.length}
                  </Badge>
                </h5>
              )}

              <Row>
                <Col md={6}>
                  <h6>Available Users</h6>
                  <ListGroup style={{ height: "600px", overflowY: "auto" }}>
                    {users.length === 0 ? (
                      <p className="text-muted p-3">No users found for this client.</p>
                    ) : availableUsers.length === 0 ? (
                      <p className="text-muted p-3">All users have been assigned to this group.</p>
                    ) : (
                      availableUsers.map((usr) => (
                        <ListGroup.Item
                          key={usr.userId}
                          className="d-flex justify-content-between align-items-center"
                        >
                          <div>
                            <strong>{usr.firstName} {usr.lastName}</strong>
                            <br />
                            <small className="text-muted">{usr.email}</small>
                          </div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleAssignUser(usr.userId)}
                          >
                            <FaPlus />
                          </Button>
                        </ListGroup.Item>
                      ))
                    )}
                  </ListGroup>
                </Col>

                <Col md={6}>
                  <h6>Assigned Users</h6>
                  <ListGroup style={{ height: "600px", overflowY: "auto" }}>
                    {groupUsers.length === 0 ? (
                      <p className="text-muted p-3">No users assigned to this group</p>
                    ) : (
                      groupUsers.map((gu) => {
                        const userDetails = getUserDetails(gu.userId);
                        return (
                          <ListGroup.Item
                            key={gu.userId}
                            className="d-flex justify-content-between align-items-center"
                          >
                            <div>
                              <strong>
                                {userDetails ? `${userDetails.firstName} ${userDetails.lastName}` : gu.userId}
                              </strong>
                              <br />
                              <small className="text-muted">
                                {userDetails?.email || `User ID: ${gu.userId}`}
                              </small>
                            </div>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleRemoveUser(gu.userId)}
                            >
                              <FaTrash />
                            </Button>
                          </ListGroup.Item>
                        );
                      })
                    )}
                  </ListGroup>
                </Col>
              </Row>
            </>
          ) : (
            <p className="text-muted">Select a permission group to manage its users</p>
          )}
        </Col>
      </Row>
    </>
  );
}

export default UserGroupsTab;
